import React, { useState, useEffect, useCallback } from "react";
import "../../static/css/App.css";
import { Container, Button, Modal, Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../static/css/customerTable.css";
import moment from "moment";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import { showConfirm } from "react-confirm-prompt";
import DynamicBreadcrumb from "../header/breadcrumb";
import DeliveryOptionForm from "./DeliveryOptionForm";
import deliveryApi from "../../services/deliveryapi";
import branchApi from "../../services/branchapi";
import Loader from "../loader/loader";
import SearchableDropdown from "../components/SearchableDropdown";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
library.add(fas);

const DeliveryOptionList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [totalCustomer, setTotalCustomer] = useState(0);

  const [refreshTable, setRefreshTable] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });

  // Inactivate reason modal
  const [openModal, setOpenModal] = useState(false);
  const [closeNotes, setCloseNotes] = useState("");
  const [errors, setErrors] = useState({});
  const [slotId, setSlotId] = useState(null);
  const [customerStatus, setCustomerStatus] = useState(null);

  // View modal
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState(null);

  // Branch + Delivery type filters
  const [branchId, setBranchID] = useState(0);
  const [branchName, setBranchName] = useState("All");
  const [allBranches, setAllBranches] = useState([]);
  const [boxBranchName, setboxBranchName] = useState(branchName);

  const [deliTypeId, setDeliTypeID] = useState(0);
  const [deliTypeName, setDeliTypeName] = useState("All Delivery Options");

  // Add/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingDeliveryOption, setEditingDeliveryOption] = useState(null);

  const [loaderVisible, setLoaderVisible] = useState(false);

  const pathArray = [{ name: "Delivery Options List", url: null }];

  const fetchDeliveryOption = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;

      const response = await deliveryApi.getDeliveryOptions({
        limit: pageSize,
        page,
        sortModel,
        filterModel,
        branchId,
        deliTypeId,
      });

      if (response?.data?.result && response.data.result.length > 0) {
        const formattedRows = response.data.result.map((cust) => {
          const deliveryType =
            Number(cust.delivery_type_id) === 4 ? "Standard Delivery" : "Express Delivery";

          return {
            id: cust.id,
            branch: cust.branch ? cust.branch.branchName : "Main Branch",
            title: cust.title,
            barrier_time: `${cust.barrier_time_before} - ${cust.barrier_time_after}`,
            created_at: moment(cust.created_at).format("YYYY/MM/DD kk:mm:ss"),
            updated_at: moment(cust.updated_at).format("YYYY/MM/DD kk:mm:ss"),
            delivery_type: deliveryType,
            StandardDeliveryFees: cust.StandardDeliveryFees || [],
            status: cust.status,
            barrier_time_before: cust.barrier_time_before,
            barrier_time_after: cust.barrier_time_after,
            branch_id: cust.branch ? cust.branch.branchId : 0,
            delivery_type_id: cust.delivery_type_id,
          };
        });

        setRows(formattedRows);
        setTotalCustomer(response.data.totalRecords?.[0]?.total_records || 0);
      } else {
        setRows([]);
        setTotalCustomer(0);
      }
    } catch (error) {
      console.error("Error fetching delivery options:", error);
      setRows([]);
      setTotalCustomer(0);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, sortModel, filterModel, branchId, deliTypeId, refreshTable]);

  useEffect(() => {
    fetchDeliveryOption();
  }, [fetchDeliveryOption]);

  useEffect(() => {
    const fetchBranch = async () => {
      try {
        const brachList = await branchApi.getAllBranches({ status: 1 });
        const branchSorted = (brachList?.data?.result || []).sort((a, b) =>
          (a.branchName || "").localeCompare(b.branchName || "")
        );
        setAllBranches(branchSorted);

        const getBranchDet = (brachList?.data?.result || []).filter((val) =>
          val.branchId === branchId ? val : null
        );
        if (getBranchDet.length > 0) {
          setBranchName(getBranchDet[0].branchName || branchName);
          setboxBranchName(getBranchDet[0].branchName || branchName);
        }
      } catch (e) {
        console.error("Failed to fetch branch:", e);
      }
    };
    fetchBranch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewClick = (deliveryOption) => {
    setSelectedDeliveryOption(deliveryOption);
    setOpenViewModal(true);
  };

  const validateCancelForm = () => {
    const newErrors = {};
    if (!closeNotes || !closeNotes.trim()) newErrors.closeNotes = "Notes is required";
    return newErrors;
  };

  const handleInactivate = async (event) => {
    event.preventDefault();
    const formErrors = validateCancelForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    setErrors({});
    await submitUpdateDetails(slotId, customerStatus, closeNotes);
  };

  const submitUpdateDetails = async (slotId, status, notes = null) => {
    try {
      setLoaderVisible(true);
      const sendStatus = Number(status) === 1 ? 2 : 1;

      const resp = await deliveryApi.updateDeliveryConfigStatus({
        id: slotId,
        status: sendStatus,
        reason: notes,
      });

      if (resp) {
        const message = resp.message || "Delivery config status updated successfully";
        showConfirm("Success", {
          description: message,
          type: "Success",
          hideCancel: true,
        }).then(() => {
          setRefreshTable((p) => !p);
          handleClose();
        });
      } else {
        showConfirm("Oops!", {
          description: "Process failed, kindly try again later!",
          type: "warning",
          hideCancel: true,
        }).then(() => {
          setRefreshTable((p) => !p);
          handleClose();
        });
      }
    } catch (error) {
      console.error(error);
      showConfirm("Oops!", {
        description: "Process failed, kindly try again later!",
        type: "warning",
        hideCancel: true,
      }).then(() => handleClose());
    } finally {
      setLoaderVisible(false);
    }
  };

  const handleStatusClick = (slotId, status) => {
    const isActive = Number(status) === 1;
    const sureTxt = isActive
      ? "Are you sure to change status to inactive?"
      : "Are you sure to change status to active?";
    const decTxt = isActive
      ? "This will inactivate the Delivery Option"
      : "This will activate the Delivery Option";

    showConfirm(sureTxt, { description: decTxt, type: "info" }).then((answer) => {
      if (!answer) return;

      setSlotId(slotId);
      setCustomerStatus(status);

      if (!isActive) {
        // Inactive -> Active (no reason required)
        submitUpdateDetails(slotId, status, null);
      } else {
        // Active -> Inactive (need reason)
        setOpenModal(true);
      }
    });
  };

  const handleClose = () => {
    setOpenModal(false);
    setCloseNotes("");
    setErrors({});
  };

  const handleViewClose = () => {
    setOpenViewModal(false);
    setSelectedDeliveryOption(null);
  };

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const handleAddClick = () => {
    setEditingDeliveryOption(null);
    setShowModal(true);
  };

  const handleEditClick = (deliveryOption) => {
    setEditingDeliveryOption(deliveryOption);
    setShowModal(true);
  };

  const handleSave = () => {
    setEditingDeliveryOption(null);
    setShowModal(false);
    setRefreshTable((p) => !p);
  };

  const DeliveryModelClose = () => {
    setShowModal(false);
    setEditingDeliveryOption(null);
  };

  const columns = [
    { field: "branch", headerName: "Branch", sortable: false, flex: 1 },
    { field: "title", headerName: "Title", sortable: true, flex: 1 },
    { field: "barrier_time", headerName: "Barrier Time (Before - After)", sortable: false, flex: 1 },
    { field: "updated_at", headerName: "Updated On", sortable: true, flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const v = parseInt(params.value, 10);
        return (
          <button
            onClick={() => handleStatusClick(params.id, v)}
            className={`btn ${v === 1 ? "btn-danger" : "btn-secondary"}`}
          >
            {v === 1 ? "Active" : "Inactive"}
          </button>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ flexDirection: "row" }} className="d-flex">
          <button className="btn btn-sm btn-primary" onClick={() => handleViewClick(params.row)}>
            View
          </button>
          <button className="btn btn-sm btn-success mx-1" onClick={() => handleEditClick(params.row)}>
            Edit
          </button>
        </Box>
      ),
    },
  ];

  return (
    <Container className="mt-4 mb-4">
      <Box className="d-flex justify-content-between align-items-center">
        <DynamicBreadcrumb pathArray={pathArray} classesName="col-7" />
        <button className="btn btn-primary mx-3" onClick={handleAddClick}>
          Add New Delivery Option
        </button>
      </Box>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>ALL Delivery Options</h5>

        <SearchableDropdown
          options={allBranches}
          extraoption={{ branchId: 0, branchName: "All" }}
          label="branchName"
          id="branchId"
          selectedVal={boxBranchName}
          handleChange={({ id = null, name = null }) => {
            setBranchID(id);
            if (name) setBranchName(name);
            setboxBranchName(name);
          }}
        />

        <SearchableDropdown
          options={[
            { id: 0, text: "All Delivery Options" },
            { id: 2, text: "Express Delivery" },
            { id: 4, text: "Standard Delivery" },
          ]}
          label="text"
          id="id"
          selectedVal={deliTypeName}
          handleChange={({ id = null, name = null }) => {
            setDeliTypeID(id);
            if (name) setDeliTypeName(name);
          }}
        />
      </div>

      {loaderVisible && <Loader />}

      <Box>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowHeight={() => "auto"}
          rowsPerPageOptions={[5, 10, 20]}
          paginationMode="server"
          rowCount={totalCustomer}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          filterMode="server"
          onSortModelChange={setSortModel}
          onFilterModelChange={handleFilterModelChange}
          autosizeOptions={{
            columns: ["mobile", "customerName", "status"],
            includeOutliers: true,
            includeHeaders: false,
          }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: false,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          onRowSelectionModelChange={(row) => setRowSelectionModel(row)}
          rowSelectionModel={rowSelectionModel}
          sx={{
            [`& .${gridClasses.cell}`]: {
              py: 0.5,
            },
          }}
        />
      </Box>

      {/* Inactivate Reason Modal */}
      <Modal show={openModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Enter Reason</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form className="cancelForm">
            <Form.Group className="mb-3" controlId="closeNotes">
              <Form.Label>Enter inactivate reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                isInvalid={!!errors.closeNotes}
              />
              <Form.Control.Feedback type="invalid">{errors.closeNotes}</Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" onClick={handleInactivate}>
            Submit Reason
          </Button>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Modal */}
      <Modal show={openViewModal} onHide={handleViewClose}>
        <Modal.Header closeButton>
          <Modal.Title>View Delivery Option</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <h6>Branch: {selectedDeliveryOption?.branch}</h6>
          <h6>Title: {selectedDeliveryOption?.title}</h6>
          <h6>Barrier Time: {selectedDeliveryOption?.barrier_time}</h6>
          <h6>Created On: {selectedDeliveryOption?.created_at}</h6>
          <h6>Updated On: {selectedDeliveryOption?.updated_at}</h6>

          <h6 className="mt-3">Standard Delivery Fees:</h6>

          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Radius</th>
                <th>Order Value</th>
                <th>Fee</th>
                <th>Threshold</th>
              </tr>
            </thead>
            <tbody>
              {(selectedDeliveryOption?.StandardDeliveryFees || []).map((fee) => (
                <tr key={fee.id}>
                  <td>
                    {fee.min_radius_km} - {fee.max_radius_km} km
                  </td>
                  <td>
                    {fee.min_order_value} - {fee.max_order_value}
                  </td>
                  <td>{fee.fee}</td>
                  <td>{fee.threshold}</td>
                </tr>
              ))}
              {(!selectedDeliveryOption?.StandardDeliveryFees ||
                selectedDeliveryOption.StandardDeliveryFees.length === 0) && (
                <tr>
                  <td colSpan={4} className="text-center">
                    No fee slabs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleViewClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add/Edit Modal */}
      <DeliveryOptionForm
        show={showModal}
        onHide={DeliveryModelClose}
        initialData={editingDeliveryOption}
        onSave={handleSave}
        allBranches={allBranches}
      />
    </Container>
  );
};

export default DeliveryOptionList;
